import store from "@/store";
import { translation } from "@/plugins/i18n";
import {
  computedCallingPlans,
  computedPbxCallingPlans,
  computedPbxAddonPlans,
  computedPbxFeaturePlans,
  computedPbxPlans,
} from '@/common/service/computers/plans/pbx';
import BillingMessageBox from '@/components/common/message-box';
import { getPbxContactSalesLink } from '@/utils/i18n-shortcuts';
import { enableP2PBuyMorePhoneLicense, planTypes } from '@/common/constants';
import { isProductExcludedInShoppingCart } from '@/views/config/shopping-cart/persist/plan-remove';
import { isInSoftBundleContext } from '@/views/config/soft-bundle/helper';
import { persistCartConstant } from "@/views/config/shopping-cart/persist/persist-cart";

export const preCheckForPbx = async () => {
  const { getSelectedPlans = [], getUserCurrentSubscriptions = [] } = store.getters;
  const { is, type } = window.$context;

  // Calculate selected PBX plans first (needed for subsequent checks)
  const selectedPbxPlans = computedPbxPlans(getSelectedPlans);

  // Determine if PBX validation is needed (OR relationship)
  // Scenario 1: type is ZOOM_PHONE
  const isZoomPhoneType = type === planTypes.ZOOM_PHONE;

  // Scenario 2: from shopping cart with PBX products
  const isShoppingCartWithPbx = persistCartConstant.enablePersistShoppingBagForV2 && selectedPbxPlans?.length > 0;

  // Scenario 3: soft bundle case with PBX products
  const isSoftBundleWithPbx = isInSoftBundleContext() && selectedPbxPlans?.length > 0;

  // Early return: skip validation if cart has excluded products,
  // UNLESS it's a shopping cart scenario with PBX products (which needs validation)
  if (isProductExcludedInShoppingCart(getSelectedPlans) && !isShoppingCartWithPbx) {
    return Promise.resolve();
  }

  const shouldValidatePbx = isZoomPhoneType || isShoppingCartWithPbx || isSoftBundleWithPbx;

  if (!shouldValidatePbx) {
    return Promise.resolve();
  }

  // Check if the total number of calling plans and ZPI plans is more than 99
  const pbxCallingPlans = computedPbxCallingPlans(getUserCurrentSubscriptions);
  const pbxAddonPlans = computedPbxAddonPlans(getSelectedPlans);
  const pbxCallingPlansCount = pbxCallingPlans.reduce((total, x) => total + (x.cartInfo.selected_number ?? 0), 0)
  const pbxAddonPlansCount = pbxAddonPlans.reduce((total, x) => total + (x.cartInfo.selected_number ?? 0), 0)

  const shouldExpandLimit = (is.edit || is.upsell) && enableP2PBuyMorePhoneLicense;
  const maxLimitedPlansCount = shouldExpandLimit ? 250 : 99;
  if (pbxCallingPlansCount && pbxCallingPlansCount + pbxAddonPlansCount > maxLimitedPlansCount) {
    if (is.configPage) {
      BillingMessageBox.alert(translation('billing.pbx.more_than_numbers', [maxLimitedPlansCount, getPbxContactSalesLink()]), translation('billing.buyItNow.cant_continue'), { dangerouslyUseHTMLString: true });
    }
    return Promise.reject({
      message: translation('billing.pbx.more_than_numbers', [maxLimitedPlansCount, getPbxContactSalesLink()]),
      duration: 5000
    })
  }

  const callingPlans = computedCallingPlans(getUserCurrentSubscriptions) ?? [];
  const callingPlansCount = callingPlans.reduce((total, x) => total + (x.cartInfo?.selected_number ?? 0), 0);
  const pbxZPAPlanCount = computedPbxFeaturePlans(getSelectedPlans).find(x => x.category === 'ZPA')?.cartInfo?.selected_number ?? 0;
  if (pbxZPAPlanCount > callingPlansCount) {
    if (is.configPage) {
      BillingMessageBox.alert(translation('billing.pbx.add_on_cant_exceed_pbx_sum'), translation('billing.buyItNow.cant_continue'));
    }
    return Promise.reject({
      message: translation('billing.pbx.add_on_cant_exceed_pbx_sum'),
      duration: 5000,
    })
  }
  return Promise.resolve()
}
